import React from "react";
import Card from "components/Card/Card";
import { useForm, SubmitHandler } from "react-hook-form";
import Page from "components/Page/Page";
import Button from "components/Button/Regular";
import Input from "components/Input/Input";
import Select from "components/Input/Select";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useActiveOrg } from "context/ActiveOrgContext";
import { createOrganisation } from "services/clinics.service";
import { readSelfProfile } from "services/profile.service";
import { updateProviderProfile, emptyProfile } from "services/provider-profile.service";

type FormValues = {
  orgName: string;
  currency: string;
  isFuneralHome: boolean;
  isCemetery: boolean;
  isCrematorium: boolean;
};

const KINDS: { key: "isFuneralHome" | "isCemetery" | "isCrematorium"; label: string }[] = [
  { key: "isFuneralHome", label: "Funeral home" },
  { key: "isCemetery", label: "Cemetery" },
  { key: "isCrematorium", label: "Crematorium" },
];

const CURRENCY_MAP: Record<string, number> = {
  euro: 978,
  dollar: 840,
  pound: 826,
};

const NewClinic = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { refreshOrganisations, setActiveOrg } = useActiveOrg();
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const {
    register,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      orgName: "",
      currency: "euro",
      isFuneralHome: false,
      isCemetery: false,
      isCrematorium: false,
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSubmitError(null);
    if (!data.isFuneralHome && !data.isCemetery && !data.isCrematorium) {
      setSubmitError("Choose at least one kind of provider: funeral home, cemetery or crematorium.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const monetaryCurrency = CURRENCY_MAP[data.currency] ?? 978;
      const { data: profile, error: profileError } = await readSelfProfile(token);
      if (profileError || !profile?.userId) {
        setSubmitError(profileError?.message ?? "Could not resolve current user");
        return;
      }
      const { data: res, error } = await createOrganisation(token, {
        orgName: data.orgName,
        monetaryCurrency,
        creatorId: profile.userId,
      });
      if (error) {
        setSubmitError(error.message);
        return;
      }
      const newId =
        (res as { newRecordID?: number; NewRecordID?: number })?.newRecordID ??
        (res as { newRecordID?: number; NewRecordID?: number })?.NewRecordID;
      if (newId != null && Number.isFinite(Number(newId))) {
        // Someone who has just created a provider means to work in it: make it the active
        // one, after re-reading the list so the header can show it.
        await refreshOrganisations();
        setActiveOrg(Number(newId), data.orgName);
        // The organisation exists now; record what kind of provider it is. A failure
        // here is shown on the details page, where the profile can be saved again.
        const { error: profileSaveError } = await updateProviderProfile(token, {
          ...emptyProfile(Number(newId)),
          isFuneralHome: data.isFuneralHome,
          isCemetery: data.isCemetery,
          isCrematorium: data.isCrematorium,
        });
        if (profileSaveError) {
          setSubmitError(`Provider created, but its kinds were not saved: ${profileSaveError.message}`);
          navigate(`/app/clinics/${newId}`);
          return;
        }
        navigate(`/app/clinics/${newId}`);
      } else {
        await refreshOrganisations();
        navigate("/app/clinics");
      }
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card
        title="Add New Provider"
        subTitle="Your funeral home, cemetery or crematorium"
        className="mt-6"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="p-10">
          <div className="grid grid-cols-1 gap-6 mb-10 sm:grid-cols-2 sm:gap-10">
            <Input
              {...register("orgName", { required: true })}
              label="Provider name"
              placeholder="e.g. Northside Funeral Home"
            />

            <Select
              {...register("currency")}
              label="Currency"
              placeholder="Currency"
              options={[
                { title: "Euro", value: "euro" },
                { title: "Dollar", value: "dollar" },
                { title: "Pound", value: "pound" },
              ]}
            />
          </div>

          <fieldset className="mb-10 flex flex-col gap-3">
            <legend className="text-slate-600 text-sm font-medium mb-1">What kind of provider is this?</legend>
            {KINDS.map(({ key, label }) => (
              <label key={key} className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" className="h-4 w-4" {...register(key)} />
                <span className="text-sm text-slate-800">{label}</span>
              </label>
            ))}
          </fieldset>

          {submitError && (
            <div className="mb-4 text-sm text-red-500">{submitError}</div>
          )}

          <Button color="Primary" type="submit" disabled={submitting}>
            <span>{submitting ? "Creating…" : "Create Provider"}</span>
          </Button>
        </form>
      </Card>
    </Page>
  );
};

export default NewClinic;
